import React, { useState, useEffect } from "react";
import {
  Box,
  Link,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Container,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BASE_URL from "../../config/apiConfig";
import { isAdminAuthenticated, saveAdminToken } from "../../utils/auth";

// Email validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ERROR_MESSAGES = {
  invalidEmail: "Please enter a valid email address",
  shortPassword: "Password must be at least 6 characters",
  invalidCredentials: "Invalid email or password.",
  generalError: "An error occurred. Please try again later.",
};

export function LoginPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    password: false,
    server: null,
  });

  // Automatically redirect if already authenticated
  useEffect(() => {
    if (isAdminAuthenticated()) {
      navigate("/admin/dashboard"); // Redirect to dashboard if already logged in
    }
  }, [navigate]);

  // Validate email
  const validateEmail = (email) => emailRegex.test(email);

  // Validate password
  const validatePassword = (password) => password.length >= 6;

  // Handle SignIn process
  const handleSignIn = async (e) => {
    e.preventDefault();

    // Reset states
    setLoading(true);
    setErrors({ email: false, password: false, server: null });

    // Email and Password Validation
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      // Set specific errors for invalid inputs
      setErrors({
        email: !isEmailValid,
        password: !isPasswordValid,
        server: null,
      });
      setLoading(false);
      return;
    }

    console.log("Initiating admin login process...");

    // API request to backend for admin authentication
    try {
      const response = await fetch(`${BASE_URL}/api/admin/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Admin login successful:", data);

        if (!data.token) {
          throw new Error("Token is missing in the response.");
        }

        // Save token and navigate to admin dashboard
        console.log(data);

        saveAdminToken(data.token, email); // Save token to cookies

        navigate("/admin/dashboard"); // Navigate to the dashboard after successful login
      } else {
        // Handle failed login attempts
        const errorData = await response.json();
        console.error("Admin login error:", errorData.message);
        setErrors((prev) => ({
          ...prev,
          server: errorData.message || "Invalid email or password.",
        }));
      }
    } catch (error) {
      console.error("Error during admin login request:", error);
      // Handle network or unexpected errors
      setErrors((prev) => ({
        ...prev,
        server: "Something went wrong. Please try again later.",
      }));
    } finally {
      setLoading(false);
    }
  };

  // Render the login form
  const renderForm = (
    <Box
      component="form"
      onSubmit={handleSignIn}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      sx={{ width: "100%", maxWidth: "400px", mb: 2 }}
    >
      <TextField
        fullWidth
        name="email"
        label="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        helperText={errors.email ? ERROR_MESSAGES.invalidEmail : ""}
        sx={{
          mb: 2,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      <TextField
        fullWidth
        name="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type={showPassword ? "text" : "password"}
        error={errors.password}
        helperText={errors.password ? ERROR_MESSAGES.shortPassword : ""}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      <Box sx={{ textAlign: "right", mb: 2 }}>
        <Link
          href="/user/recoverpassword"
          variant="body2"
          color="inherit"
          sx={{
            fontSize: "0.9rem",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline", color: "#51833a" },
          }}
        >
          Forgot password?
        </Link>
      </Box>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="inherit"
        variant="contained"
        loading={loading}
        sx={{
          background: "linear-gradient(45deg, #51833a 30%, #66bb6a 90%)",
          color: "#fff",
          "&:hover": { backgroundColor: "#406027" },
          mb: 2,
          textTransform: "none",
          minHeight: "50px",
          borderRadius: "8px",
          transition: "0.3s ease",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow effect
        }}
      >
        Sign in
      </LoadingButton>

      {errors.server && (
        <Typography
          variant="body2"
          color="error"
          align="center"
          aria-live="polite"
        >
          {errors.server}
        </Typography>
      )}
    </Box>
  );

  return (
    <Container
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: 2, md: 0 },
        background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)", // Gradient background
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          minHeight: "70vh",
          borderRadius: "16px",
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "900px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            padding: { xs: 3, md: 5 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            backgroundColor: "#f9fafb",
          }}
        >
          <Box
            gap={1.5}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            sx={{ mb: 5 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#333",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Admin Sign in
            </Typography>
          </Box>

          {renderForm}
        </Box>

        <Box
          sx={{
            backgroundColor: "#e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            padding: { xs: 2, md: 4 },
            position: "relative",
          }}
        >
          <Box
            component="img"
            src="/Images/Login_Body.webp"
            alt="Admin Login Illustration"
            sx={{
              maxWidth: "80%",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          />
        </Box>
      </Box>
    </Container>
  );
}

export default LoginPage;
